import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateSubscription } from "@factory/query/subscription.query";
import {
  MemberFormSchema,
  SubscriptionFormRule,
  SubscriptionFormSchema,
} from "@factory/form/form.rule";
import { subscriptionFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useQyCreateMember } from "@factory/query/member.query";
import { GetMemberModel } from "@factory/model/query-model-member";
import { MessageResponseDto } from "@api/api";
import { useCallback, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import {
  CreateTransactionWaiverModel,
  CreateTransactionWaiverModelExtension,
} from "@factory/model/query-model-transaction-waiver";
import { useQyCreateTransactionWaiver } from "@factory/query/transaction-waiver.query";
import { MutateResponseSubscriptionModel } from "@factory/model/query-model-subscription";
import { useTransactionStepContext } from "@domain/transaction/new-transaction/transaction-step.context";
import { getBirthdateFromAge } from "@shared/utility/date-time-service/birthday";

export function useNewSubscription() {
  const {
    selectedMember,
    memberFormData,
    serviceFormData,
    selectedService,
    selectedWaiverForm,
    setSelectedMember,
    setMemberFormData,
    previousStepper,
    clearLocalStorage,

    // Persisted states from step context
    isCreateNewMember,
    setIsCreateNewMember,
    subscriptionStartDate,
    setSubscriptionStartDate,
  } = useTransactionStepContext();

  const navigate = useNavigate();
  const { mapCreateSubscription, mapCreateMember } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  const [isCreating, setIsCreating] = useState(false);
  const [memberInfo, setMemberInfo] = useState<GetMemberModel | null>(null);

  const componentRef = useRef(null);

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;
    setIsCreating(false);
    clearLocalStorage();
    showSuccess(`New Subscription is created`);
    navigate(`../${data.code}`);
  };
  const { mutateAsync: addSubscription } =
    useQyCreateSubscription(handleApiSuccess);
  const { mutateAsync: addMember } = useQyCreateMember();
  const { mutateAsync: newWaiver } = useQyCreateTransactionWaiver();

  // LOCAL FUNCTIONS
  const formMethod = useForm<SubscriptionFormSchema>({
    defaultValues: subscriptionFormDefault,
    resolver: zodResolver(SubscriptionFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: SubscriptionFormSchema) => {
    const formData = mapCreateSubscription(form);
    addSubscription(formData);
  };
  const handleValidateError = (err: FieldErrors<SubscriptionFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  // Create member function
  const createMember = useCallback(async () => {
    setIsCreating(true);
    let birthday = memberFormData?.birthday;
    if (memberFormData?.age && !memberFormData.birthday) {
      birthday = getBirthdateFromAge(memberFormData.age).toString();
    }

    const memberPayload = mapCreateMember({
      ...memberFormData,
      birthday,
      is_child: false,
    } as MemberFormSchema);
    const member = await addMember(memberPayload);
    const memberData = member.data as GetMemberModel;

    let fileBlob = undefined;
    if (memberData.avatar) {
      const elementFile = await viewAsset(memberData.avatar);
      fileBlob = URL.createObjectURL(elementFile);
    }

    setMemberInfo({ ...memberData, fileBlob });
    setSelectedMember(memberData.code || "");
    setIsCreating(false);
    setMemberFormData(null);
  }, [
    memberFormData,
    mapCreateMember,
    addMember,
    setMemberInfo,
    setSelectedMember,
    setMemberFormData,
  ]);

  // Create subscription from services
  const createSubscriptionFromServices = useCallback(
    async (isPaid: boolean) => {
      if (!selectedWaiverForm) {
        showWarning("Missing waiver. Please select one");
        previousStepper();
        return;
      }

      if (!subscriptionStartDate) {
        showWarning("Please select subscription start date");
        previousStepper();
        return;
      }
      const firstService = serviceFormData[0];
      console.log("firstService:", firstService);

      const selectedServiceCode =
        serviceFormData[0]?.service || serviceFormData[0]?.serviceData?.code;
      console.log("selectedServiceCode:", selectedServiceCode);

      setIsCreating(true);

      const form = mapCreateSubscription({
        member: selectedMember,
        service: selectedServiceCode,
        startDate: subscriptionStartDate,
        isSubscription: true,
        // isPaid,
      });

      const response: MutateResponseSubscriptionModel = await addSubscription(
        form
      );
      const dataResponse = response.data;

      const updatedWaiver = {
        ...selectedWaiverForm?.waiver,
        batch: (dataResponse as any)?.code,
      } as CreateTransactionWaiverModel;
      const updatedWaiverForm = {
        ...selectedWaiverForm,
        waiver: updatedWaiver,
      } as CreateTransactionWaiverModelExtension;
      newWaiver(updatedWaiverForm);

      setIsCreating(false);
    },
    [
      selectedWaiverForm,
      subscriptionStartDate,
      serviceFormData,
      mapCreateSubscription,
      selectedMember,
      serviceFormData,
      addSubscription,
      newWaiver,
      showWarning,
      previousStepper,
    ]
  );

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  return {
    isCreating,
    componentRef,
    isCreateNewMember,
    memberInfo,
    subscriptionStartDate,
    setIsCreating,
    setMemberInfo,
    setIsCreateNewMember,
    setSubscriptionStartDate,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
    handlePrint,
    createSubscriptionFromServices,
    createMember,
  };
}
